import { AndOrType, ExistenceOperator } from "@repo/zod-filters";

//#region src/attributes.ts
let FilterComponentType = /* @__PURE__ */ function(FilterComponentType) {
	FilterComponentType[FilterComponentType["ButtonGroup"] = 1] = "ButtonGroup";
	FilterComponentType[FilterComponentType["NumberComparison"] = 2] = "NumberComparison";
	FilterComponentType[FilterComponentType["Toggle"] = 3] = "Toggle";
	FilterComponentType[FilterComponentType["Select"] = 4] = "Select";
	FilterComponentType[FilterComponentType["Input"] = 5] = "Input";
	FilterComponentType[FilterComponentType["Checkbox"] = 6] = "Checkbox";
	FilterComponentType[FilterComponentType["CheckboxList"] = 7] = "CheckboxList";
	FilterComponentType[FilterComponentType["RangeSlider"] = 8] = "RangeSlider";
	return FilterComponentType;
}({});
let InfoConditionType = /* @__PURE__ */ function(InfoConditionType) {
	InfoConditionType["NoSelection"] = "noSelection";
	InfoConditionType["HasSelection"] = "hasSelection";
	InfoConditionType["SpecificValue"] = "specificValue";
	InfoConditionType["SpecificValueCount"] = "specificValueCount";
	return InfoConditionType;
}({});
function enumAttribute(_inputEnum, attr) {
	return attr;
}

//#endregion
//#region src/get-selectable-attributes.ts
/**
* Returns a list of all attributes by types from an attribute list
* @param attributes List of attributes
* @returns List of all attributes by types in the given list
*/
const getAttributesByTypes = (attributes, types) => {
	const selectableAttributes = {};
	Object.entries(attributes).forEach(([key, value]) => {
		if (value && types.includes(value.type)) selectableAttributes[key] = attributes[key];
	});
	return selectableAttributes;
};
/**
* Returns a list of all selectable attributes from an attribute list
* @param attribute list
* @returns List of all selectable attributes in the given list
*/
const getSelectableAttributes = (attributes) => {
	const selectableAttributes = {};
	Object.entries(attributes).forEach(([key, value]) => {
		if (value?.selectable !== false) selectableAttributes[key] = attributes[key];
	});
	return selectableAttributes;
};
/**
* Returns a list of all filterable attributes from an attribute list
* @param attribute list
* @returns List of all filterable attributes in the given list
*/
const getFilterableAttributes = (attributes) => {
	const filterableAttributes = {};
	Object.entries(attributes).forEach(([key, value]) => {
		if (!value) return;
		if (value.filter !== void 0) filterableAttributes[key] = attributes[key];
	});
	return filterableAttributes;
};
const getGroupableAttributes = (attributes) => {
	const groupableAttributes = {};
	const keyByName = {};
	Object.entries(attributes).forEach(([key, value]) => {
		if (!value) return;
		if (value.filter === void 0 || key === "name") return;
		const existingKey = keyByName[value.name];
		if (existingKey) {
			const existing = groupableAttributes[existingKey];
			if (existing && !existing.array) return;
			delete groupableAttributes[existingKey];
		}
		keyByName[value.name] = key;
		groupableAttributes[key] = value;
	});
	return groupableAttributes;
};
/**
* Merges multiple attribute objects into a single attribute object
* @param attributes Source attributes to be merged
* @returns Single attribute object
*/
const mergeAttributes = (...attributes) => {
	const mergedAttributes = {};
	attributes.forEach((attribute) => {
		Object.entries(attribute).forEach(([key, value]) => {
			mergedAttributes[key] = value;
		});
	});
	return mergedAttributes;
};

//#endregion
//#region src/set-match.ts
/**
* Narrows an attribute to an enum attribute configured with `setMatch`.
*/
function isSetMatchAttribute(attribute) {
	return attribute?.type === "enum" && attribute.setMatch !== void 0;
}
/**
* Turns an enum filter on a `setMatch` attribute into set constraints.
*
* - `or` (subset): the item's values must all be selected or `alwaysFits`.
* - `and` (exact): the item's values must be exactly the selection. `alwaysFits`
*   values are ignored unless they are the only ones selected.
*
* Items with no value never match; query builders enforce that separately.
*/
function resolveSetMatch(attribute, filter) {
	const alwaysFits = attribute.setMatch.alwaysFits;
	const values = filter.values.map(String);
	if (filter.op === AndOrType.AND) {
		const selected = values.filter((value) => !alwaysFits.includes(value));
		const exact = selected.length > 0 ? selected : values;
		return {
			allowed: exact,
			required: exact
		};
	}
	return {
		allowed: [...new Set([...values, ...alwaysFits])],
		required: []
	};
}

//#endregion
//#region src/utils.ts
let AttributeContextKey = /* @__PURE__ */ function(AttributeContextKey) {
	AttributeContextKey["DAILY_MOVERS"] = "DAILY_MOVERS";
	return AttributeContextKey;
}({});
/**
* Rounds a number to a specified number of decimal places
* Handles floating point precision issues
*/
const roundToDecimalPlaces = (num, decimalPlaces = 2) => {
	const factor = 10 ** decimalPlaces;
	return Math.round((num + Number.EPSILON) * factor) / factor;
};
/**
* Utility function to apply context-specific attribute overrides
* This allows components to override properties like 'hidden' based on their context
*
* @param attributes - The base attributes to override
* @param overrides - The attribute overrides to apply
* @returns A new Attributes object with overrides applied
*/
const applyAttributeOverrides = (attributes, overrides) => {
	const result = {};
	for (const [key, attribute] of Object.entries(attributes)) {
		const override = overrides[key];
		result[key] = override ? {
			...attribute,
			...override
		} : attribute;
	}
	return result;
};
/**
* Context-specific attribute configurations
* These can be used to override default attribute behavior in specific contexts
*/
const ATTRIBUTE_CONTEXTS = { [AttributeContextKey.DAILY_MOVERS]: {
	change24hUs: { hidden: false },
	change24hEu: { hidden: false },
	change7dUs: { hidden: false },
	change7dEu: { hidden: false },
	change30dUs: { hidden: false },
	change30dEu: { hidden: false }
} };
/**
* Predefined value transformations for common use cases
*/
const VALUE_TRANSFORMS = {
	PERCENTAGE: {
		toBackend: (uiValue) => {
			return roundToDecimalPlaces(uiValue / 100, 6);
		},
		toUI: (backendValue) => {
			return roundToDecimalPlaces(backendValue * 100, 2);
		}
	},
	CENTS: {
		toBackend: (uiValue) => {
			return Math.round(uiValue * 100);
		},
		toUI: (backendValue) => {
			return roundToDecimalPlaces(backendValue / 100, 2);
		}
	},
	CURRENCY: {
		toBackend: (uiValue, conversionMethod) => {
			return conversionMethod(uiValue);
		},
		toUI: (backendValue, conversionMethod) => {
			return conversionMethod(backendValue);
		}
	}
};
/**
* The values an attribute declares, or null when it constrains nothing.
*
* Shared by the URL filter sanitiser and by importers: a value outside this set
* is rejected by the API's Zod enums, so both ends check against the same
* declaration rather than a hand-maintained copy.
*/
function getAllowedAttributeValues(attribute) {
	if (attribute.type === "enum") {
		const keys = Object.keys(attribute.values);
		return keys.length > 0 ? new Set(keys) : null;
	}
	if (attribute.type === "relation" && attribute.values.length > 0) return new Set(attribute.values.flatMap((option) => option.slug ? [String(option.value), option.slug] : [String(option.value)]));
	return null;
}

//#endregion
export { ATTRIBUTE_CONTEXTS, AttributeContextKey, ExistenceOperator, FilterComponentType, InfoConditionType, VALUE_TRANSFORMS, applyAttributeOverrides, enumAttribute, getAllowedAttributeValues, getAttributesByTypes, getFilterableAttributes, getGroupableAttributes, getSelectableAttributes, isSetMatchAttribute, mergeAttributes, resolveSetMatch, roundToDecimalPlaces };
//# sourceMappingURL=index.js.map